<template>
  <div class="category-tabs">
    <!-- Category Navigation -->
    <div class="category-legend d-flex align-items-center mb-4">
      <div class="mr-3 d-flex align-items-center">
        <b-badge variant="primary" class="mr-1">#</b-badge>
        <small class="text-muted">liczba kluczy w kategorii</small>
      </div>
      <div class="d-flex align-items-center mr-3">
        <b-badge variant="danger" class="mr-1">#</b-badge>
        <small class="text-muted">liczba brakujących tłumaczeń</small>
      </div>
      <div class="d-flex align-items-center">
        <b-badge variant="success" class="mr-1">✅</b-badge>
        <small class="text-muted">posiada wszystkie tłumaczenia</small>
      </div>
    </div>
    <div class="category-nav-container mb-4">
      <div class="category-nav">
        <div
          v-for="(category, categoryId) in categoriesWithCounts"
          :key="categoryId"
          :active="activeCategory === categoryId"
          @click="$emit('category-changed', categoryId)"
          class="category-tab"
          :class="{ active: activeCategory === categoryId }">
          <feather-icon :icon="category.icon" size="16" class="mr-1" />
          <span class="category-name">{{ category.name }}</span>

          <!-- Translation count badge -->
          <b-badge variant="primary" class="ml-2" v-if="category.count && category.count.total > 0">
            {{ category.count.total }}
          </b-badge>

          <!-- Missing translations indicator -->
          <b-badge
            v-if="category.count && category.count.missing > 0"
            variant="danger"
            class="ml-1"
            :title="`Posiada ${category.count.missing} brakujących tłumaczeń`">
            {{ category.count.missing }}
          </b-badge>
          <b-badge
            v-else
            variant="success"
            class="ml-1"
            title="Brak brakujących tłumaczeń">
            ✅ 
          </b-badge>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { translationCategories } from '@/utils/translations';

export default {
  name: 'CategoryTabs',
  props: {
    activeCategory: {
      type: String,
      default: 'general',
    },
    translationCounts: {
      type: Object,
      default: () => ({}),
    },
    getTranslationsForCategory: {
      type: Function,
      required: false,
    },
    getTranslationsForKey: {
      type: Function,
      required: false,
    },
    availableLanguages: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      activeTabIndex: 0,
    };
  },
  computed: {
    categoriesWithCounts() {
      const categories = {};

      Object.keys(translationCategories).forEach((categoryId) => {
        let count = this.translationCounts[categoryId] || { total: 0, missing: 0, percentage: 100 };

        // If incomplete filter is active and we have the necessary functions,
        // calculate dynamic counts based on filtered data
        if (this.getTranslationsForCategory && this.getTranslationsForKey) {
          const filteredTranslations = this.getTranslationsForCategory(categoryId);
          const filteredCount = Object.keys(filteredTranslations).length;

          // Calculate missing translations for filtered data
          let filteredMissing = 0;
          Object.keys(filteredTranslations).forEach((key) => {
            const allTranslationsForKey = this.getTranslationsForKey(key);
            this.availableLanguages.forEach((lang) => {
              if (lang.lang !== 'pl') {
                const translation = allTranslationsForKey[lang.locale];
                if (!translation || translation.trim() === '') {
                  filteredMissing++;
                }
              }
            });
          });

          categories[categoryId] = {
            ...translationCategories[categoryId],
            count: {
              total: filteredCount,
              missing: filteredMissing,
              percentage:
                filteredCount > 0 ? Math.round(((filteredCount - filteredMissing) / filteredCount) * 100) : 100,
            },
          };
        } else {
          categories[categoryId] = {
            ...translationCategories[categoryId],
            count,
          };
        }
      });

      return categories;
    },

    totalCount() {
      return Object.values(this.translationCounts).reduce((sum, count) => {
        return sum + (count.total || count || 0);
      }, 0);
    },

    maxCount() {
      const counts = Object.values(this.translationCounts).map((count) => count.total || count || 0);
      return Math.max(...counts, 1);
    },

    totalMissingCount() {
      return Object.values(this.translationCounts).reduce((sum, count) => {
        return sum + (count.missing || 0);
      }, 0);
    },
  },
  watch: {
    activeCategory: {
      immediate: true,
      handler(newCategory) {
        const categoryIds = Object.keys(this.categoriesWithCounts);
        this.activeTabIndex = categoryIds.indexOf(newCategory);
      },
    },
  },
};
</script>

<style scoped>
.category-card {
  cursor: pointer;
  transition: all 0.3s ease;
  border: 2px solid transparent;
}

.category-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.category-nav-container {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.category-nav {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap !important;
  max-width: 500px;
  min-width: fit-content;
  gap: 1rem;
}

.category-tab {
  font-weight: 500;
  max-width: 500px;
  transition: all 0.3s ease;
  white-space: nowrap;
  padding: 0.5rem;
  border-bottom: 1px solid #e9ecef;
  border-radius: 0.375rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  margin-bottom: 0.5rem;
  border-radius: 0.375rem;
  border: 1px solid #dee2e6;
}

.category-tab:hover {
  background-color: rgba(115, 103, 240, 0.1);
  border-color: rgba(115, 103, 240, 0.2);
}

.category-tab.active {
  background-color: white;
  /* stronger elevation */
  border-bottom: 3px solid #d29a69;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.24) !important;
}

.category-name {
  display: inline-block;
}

/* Large screens - wrap tabs */
@media (min-width: 1200px) {
  .category-nav {
    flex-wrap: wrap;
  }

  .category-tab {
    flex: 0 0 auto;
  }
}

/* Medium screens - horizontal scroll */
@media (min-width: 768px) and (max-width: 1199px) {
  .category-nav-container {
    overflow-x: auto;
    padding-bottom: 0.5rem;
  }

  .category-nav {
    flex-wrap: nowrap;
    width: max-content;
  }

  .category-tab {
    flex: 0 0 auto;
    margin-right: 0.25rem;
  }
}

/* Small screens - compact wrapping */
@media (max-width: 767px) {
  .category-nav {
    flex-wrap: wrap;
  }

  .category-tab {
    text-align: center;
    margin: 0.25rem;
    font-size: 0.875rem;
  }

  .category-tab .feather-icon {
    margin-right: 0.25rem;
  }
}

/* Extra small screens - full width stacked */
@media (max-width: 575px) {
  .category-tab {
    margin: 0.125rem 0;
  }
}

/* Custom scrollbar for horizontal scroll */
.category-nav-container::-webkit-scrollbar {
  height: 6px;
}

.category-nav-container::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.category-nav-container::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.category-nav-container::-webkit-scrollbar-thumb:hover {
  background: #a1a1a1;
}
</style>
